import type { Execution, Trade } from '../types';

// Reconstruct position episodes separately for each account and futures contract.
// Exit fills match entry lots FIFO. Commission is apportioned per filled contract.
export function reconstructTrades(executions: Execution[], pointValue = 2): Trade[] {
  const sorted = [...executions].sort((a, b) => a.timestamp - b.timestamp || a.executionId.localeCompare(b.executionId));
  const seen = new Set<string>();
  const books = new Map<string, { trade?: Trade; lots: Array<{ price: number; quantity: number; feePerUnit: number }>; signed: number; exitValue: number; exited: number }>();
  const trades: Trade[] = [];
  for (const e of sorted) {
    const identity = `${e.accountId}|${e.executionId}`;
    if (seen.has(identity) || e.quantity <= 0 || !Number.isFinite(e.price)) continue;
    seen.add(identity);
    const key = `${e.accountId}|${e.contract}`;
    const book = books.get(key) ?? { lots: [], signed: 0, exitValue: 0, exited: 0 };
    books.set(key, book);
    const delta = e.side === 'buy' ? e.quantity : -e.quantity;
    const feePerUnit = e.commission / e.quantity;
    let remaining = e.quantity;
    if (book.signed !== 0 && Math.sign(delta) !== Math.sign(book.signed)) {
      const closing = Math.min(remaining, Math.abs(book.signed));
      let toMatch = closing;
      let gross = 0;
      let entryFees = 0;
      while (toMatch > 0) {
        const lot = book.lots[0];
        const matched = Math.min(toMatch, lot.quantity);
        gross += (book.signed > 0 ? e.price - lot.price : lot.price - e.price) * matched * pointValue;
        entryFees += lot.feePerUnit * matched;
        lot.quantity -= matched;
        toMatch -= matched;
        if (lot.quantity === 0) book.lots.shift();
      }
      const trade = book.trade!;
      trade.realizedPnl += gross - entryFees - feePerUnit * closing;
      trade.commission += feePerUnit * closing;
      book.exitValue += e.price * closing;
      book.exited += closing;
      book.signed += book.signed > 0 ? -closing : closing;
      remaining -= closing;
      if (book.signed === 0) {
        trade.status = 'closed';
        trade.exitTime = e.timestamp;
        trade.exitPrice = book.exitValue / book.exited;
        book.trade = undefined;
        book.exitValue = 0;
        book.exited = 0;
      }
    }
    if (remaining > 0) {
      if (!book.trade) {
        book.trade = { id: `trade-${e.accountId}-${e.contract}-${e.executionId}${remaining < e.quantity ? '-reverse' : ''}`,
          accountId: e.accountId, instrument: e.instrument, contract: e.contract,
          direction: delta > 0 ? 'long' : 'short', entryTime: e.timestamp, entryPrice: e.price,
          quantity: remaining, realizedPnl: 0, commission: 0, status: 'open', tags: [] };
        trades.push(book.trade);
      } else {
        const old = Math.abs(book.signed);
        book.trade.entryPrice = (book.trade.entryPrice * old + e.price * remaining) / (old + remaining);
        book.trade.quantity = Math.max(book.trade.quantity, old + remaining);
      }
      book.lots.push({ price: e.price, quantity: remaining, feePerUnit });
      book.trade.commission += feePerUnit * remaining;
      book.signed += delta > 0 ? remaining : -remaining;
    }
  }
  return trades;
}
